import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert, Box, Button, Chip, CircularProgress, Divider, FormControlLabel, InputAdornment,
  Paper, Skeleton, Snackbar, Stack, Switch, TextField, Typography,
} from "@mui/material";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import SpaOutlinedIcon from "@mui/icons-material/SpaOutlined";
import StoreSettingsAPI from "../../../api/firebase/StoreSettingsAPI";

const DEFAULT_FREE_SHIPPING_ABOVE = Number(process.env.REACT_APP_FREE_SHIPPING_ABOVE) || 500;
const defaults = { freeShippingAbove: DEFAULT_FREE_SHIPPING_ABOVE, deliveryFee: 0 };
const validAmount = (value) => String(value).trim() !== "" && Number.isFinite(Number(value)) && Number(value) >= 0;
const cardStyle = { border: "1px solid #e2e8f0", borderRadius: 3, bgcolor: "#ffffff" };
const fieldStyle = { "& .MuiOutlinedInput-root": { bgcolor: "#f8fafc", borderRadius: 2 } };

const StoreSettings = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(defaults);
  const [savedSettings, setSavedSettings] = useState(defaults);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [propertiesSectionEnabled, setPropertiesSectionEnabled] = useState(true);
  const [savingPropertiesSection, setSavingPropertiesSection] = useState(false);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const storeApi = new StoreSettingsAPI();
      const result = await storeApi.getShippingSettings();
      if (!result.success) throw new Error("Load failed");
      const data = result.data || {};
      const loaded = {
        freeShippingAbove: validAmount(data.freeShippingAbove) ? Number(data.freeShippingAbove) : defaults.freeShippingAbove,
        deliveryFee: validAmount(data.deliveryFee) ? Number(data.deliveryFee) : defaults.deliveryFee,
      };
      setSettings(loaded);
      setSavedSettings(loaded);

      try {
        if (typeof storeApi.getPropertiesSectionSettings === "function") {
          const propResult = await storeApi.getPropertiesSectionSettings();
          if (propResult && propResult.success && propResult.data) {
            setPropertiesSectionEnabled(Boolean(propResult.data.isEnabled ?? propResult.data.enabled ?? true));
          }
        }
      } catch (propErr) {
        console.log("Could not load properties section settings in StoreSettings:", propErr);
      }
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleTogglePropertiesSection = async () => {
    if (savingPropertiesSection) return;
    const nextVal = !propertiesSectionEnabled;
    setSavingPropertiesSection(true);
    try {
      const storeApi = new StoreSettingsAPI();
      if (typeof storeApi.setPropertiesSectionSettings === "function") {
        const res = await storeApi.setPropertiesSectionSettings({ isEnabled: nextVal });
        if (res && res.success) {
          setPropertiesSectionEnabled(nextVal);
          setMessage({
            type: "success",
            text: `"Shop By Properties" section ${nextVal ? "enabled" : "disabled"} on homepage.`,
          });
        } else {
          setMessage({
            type: "error",
            text: "Could not update properties section setting.",
          });
        }
      } else {
        setPropertiesSectionEnabled(nextVal);
      }
    } catch {
      setMessage({
        type: "error",
        text: "Could not update properties section setting.",
      });
    } finally {
      setSavingPropertiesSection(false);
    }
  };

  useEffect(() => { loadSettings(); }, [loadSettings]);

  const valid = validAmount(settings.freeShippingAbove) && validAmount(settings.deliveryFee);
  const dirty = Object.keys(defaults).some((key) => !validAmount(settings[key]) || Number(settings[key]) !== savedSettings[key]);
  const disabled = loading || loadError || saving;

  const handleSave = async (event) => {
    event.preventDefault();
    if (!valid || disabled || !dirty) return;
    const next = { freeShippingAbove: Number(settings.freeShippingAbove), deliveryFee: Number(settings.deliveryFee) };
    setSaving(true);
    try {
      const result = await new StoreSettingsAPI().setShippingSettings(next);
      if (!result.success) throw new Error("Save failed");
      setSavedSettings(next);
      setSettings(next);
      setMessage({ type: "success", text: "Shipping settings saved." });
    } catch {
      setMessage({ type: "error", text: "Could not save shipping settings. Please try again." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ pb: 4 }}>
      <Box sx={{ mb: 3, p: { xs: 2.5, sm: 3.5 }, borderRadius: 4, color: "#ffffff",
        background: "linear-gradient(135deg, #00382e 0%, #004d40 60%, #056152 100%)",
        boxShadow: "0 10px 25px -5px rgba(0, 77, 64, 0.3)", display: { xs: "none", sm: "flex" },
        flexDirection: { xs: "column", md: "row" }, justifyContent: "space-between", alignItems: { xs: "flex-start", md: "center" }, gap: 2.5 }}>
        <Box>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Box sx={{ p: 0.5, borderRadius: "6px", bgcolor: "rgba(255,255,255,0.15)", display: "inline-flex" }}><SettingsOutlinedIcon fontSize="small" /></Box>
            <Typography variant="overline" sx={{ fontWeight: 800, letterSpacing: "0.1em", color: "#a7f3d0", fontSize: "0.75rem" }}>STORE CONFIGURATION</Typography>
          </Stack>
          <Typography component="h1" variant="h4" sx={{ fontWeight: 800, fontSize: { xs: "1.75rem", sm: "2.2rem" }, letterSpacing: "-0.02em" }}>Store Settings</Typography>
          <Typography variant="body2" sx={{ opacity: 0.88, mt: 0.5, maxWidth: 520 }}>Manage delivery fees and free shipping rules for your storefront.</Typography>
        </Box>
        <Chip icon={<LocalShippingOutlinedIcon />} label="Shipping & delivery" sx={{ bgcolor: "rgba(255,255,255,0.15)", color: "#ffffff", fontWeight: 700, "& .MuiChip-icon": { color: "#a7f3d0" } }} />
      </Box>

      {loadError && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} action={<Button color="inherit" onClick={loadSettings}>Retry</Button>}>Could not load shipping settings. Retry to edit your current rules.</Alert>}

      {/* Settings Grid: Shipping Rules and Homepage Sections side by side */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "repeat(2, 1fr)" },
          gap: 3,
          alignItems: "stretch",
        }}
      >
        {/* Shipping rules */}
        <Paper
          component="form"
          onSubmit={handleSave}
          noValidate
          elevation={0}
          sx={{
            ...cardStyle,
            display: "flex",
            flexDirection: "column",
            height: "100%",
          }}
          aria-busy={loading || saving}
        >
          <Box sx={{ p: { xs: 2.5, sm: 3 }, flex: 1 }}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 3 }}>
              <Box sx={{ p: 1, display: "flex", borderRadius: 2, bgcolor: "#ecfdf5", color: "#004d40" }}><LocalShippingOutlinedIcon /></Box>
              <Box><Typography component="h2" variant="h6" sx={{ fontWeight: 800, color: "#1e293b" }}>Shipping rules</Typography><Typography variant="body2" sx={{ color: "#64748b" }}>Set the amounts customers see in their cart.</Typography></Box>
            </Stack>
            {loading ? <Stack spacing={2} aria-label="Loading shipping settings"><Skeleton variant="rounded" height={80} /><Skeleton variant="rounded" height={80} /></Stack> : <Stack spacing={3}>
              {[
                { key: "freeShippingAbove", label: "Free shipping minimum", help: "Orders at or above this amount qualify. Set to ₹0 for free shipping on all orders." },
                { key: "deliveryFee", label: "Delivery fee", help: "Charged below the minimum. Set to ₹0 to offer free delivery regardless of order value." },
              ].map((field) => <TextField key={field.key} id={field.key} label={field.label} type="number" value={settings[field.key]} disabled={disabled}
                onChange={(event) => setSettings({ ...settings, [field.key]: event.target.value })}
                error={!validAmount(settings[field.key])} helperText={!validAmount(settings[field.key]) ? "Enter an amount of ₹0 or more." : field.help}
                inputProps={{ min: 0, step: "0.01" }} InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} fullWidth sx={fieldStyle} />)}
            </Stack>}
          </Box>
          <Divider sx={{ borderColor: "#e2e8f0" }} />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ xs: "stretch", sm: "center" }} justifyContent="space-between" flexWrap="wrap" sx={{ p: 2.5, gap: 1.5 }}>
            <Typography role="status" variant="caption" sx={{ color: dirty ? "#b45309" : "#64748b", fontWeight: 700 }}>{loading ? "Loading settings…" : loadError ? "Settings unavailable" : saving ? "Saving changes…" : dirty ? "Unsaved changes" : "Settings are up to date"}</Typography>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button disabled={disabled || !dirty} onClick={() => setSettings(savedSettings)} sx={{ color: "#475569", textTransform: "none", fontWeight: 700 }}>Reset changes</Button>
              <Button type="submit" variant="contained" disabled={disabled || !valid || !dirty} startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveOutlinedIcon />} sx={{ bgcolor: "#004d40", borderRadius: "10px", textTransform: "none", fontWeight: 800, px: 2.5, py: 1.1, boxShadow: "0 4px 12px rgba(0,77,64,0.2)", "&:hover": { bgcolor: "#00382e" } }}>{saving ? "Saving…" : "Save settings"}</Button>
            </Stack>
          </Stack>
        </Paper>

        {/* Homepage Sections */}
        <Paper
          elevation={0}
          sx={{
            ...cardStyle,
            display: "flex",
            flexDirection: "column",
            height: "100%",
          }}
        >
          <Box sx={{ p: { xs: 2.5, sm: 3 }, flex: 1, display: "flex", flexDirection: "column" }}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 3 }}>
              <Box sx={{ p: 1, display: "flex", borderRadius: 2, bgcolor: "#ecfdf5", color: "#004d40" }}>
                <SpaOutlinedIcon />
              </Box>
              <Box>
                <Typography component="h2" variant="h6" sx={{ fontWeight: 800, color: "#1e293b" }}>
                  Homepage Sections
                </Typography>
                <Typography variant="body2" sx={{ color: "#64748b" }}>
                  Control the visibility of dynamic content sections on the storefront homepage.
                </Typography>
              </Box>
            </Stack>

            <Box
              sx={{
                p: 2.5,
                borderRadius: 2,
                bgcolor: "#f8fafc",
                border: "1px solid #e2e8f0",
                flex: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                gap: 2.5,
              }}
            >
              <Box>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ gap: 1, mb: 1 }}>
                  <Typography variant="subtitle1" fontWeight={700} color="#1e293b">
                    &quot;Shop By Properties&quot; Section
                  </Typography>
                  <Chip
                    label={propertiesSectionEnabled ? "Active on Homepage" : "Hidden"}
                    size="small"
                    color={propertiesSectionEnabled ? "success" : "default"}
                    sx={{ fontWeight: 700, fontSize: "0.75rem" }}
                  />
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  Showcase curated Ayurvedic property collections (e.g. Body Detox, Hair Care, Skin Care) in an interactive collage on the homepage.
                </Typography>
              </Box>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems={{ xs: "flex-start", sm: "center" }}
                justifyContent="space-between"
                sx={{
                  pt: 2,
                  borderTop: "1px solid #e2e8f0",
                  gap: 1.5,
                }}
              >
                <Button
                  onClick={() => navigate("/admin/properties")}
                  variant="outlined"
                  size="small"
                  sx={{ textTransform: "none", fontWeight: 700, borderColor: "#cbd5e1", color: "#475569" }}
                >
                  Manage Properties
                </Button>
                <FormControlLabel
                  control={
                    <Switch
                      checked={propertiesSectionEnabled}
                      onChange={handleTogglePropertiesSection}
                      disabled={loading || savingPropertiesSection}
                      color="success"
                      inputProps={{ "aria-label": "Toggle Shop By Properties Section" }}
                    />
                  }
                  label={
                    <Typography fontWeight={700} fontSize="0.875rem" color="#1e293b">
                      {propertiesSectionEnabled ? "Enabled" : "Disabled"}
                    </Typography>
                  }
                  sx={{ mr: 0 }}
                />
              </Stack>
            </Box>
          </Box>
        </Paper>
      </Box>

      <Snackbar open={Boolean(message)} autoHideDuration={5000} onClose={() => setMessage(null)} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert variant="filled" severity={message?.type || "info"} onClose={() => setMessage(null)} sx={{ borderRadius: 2 }}>{message?.text}</Alert>
      </Snackbar>
    </Box>
  );
};

export default StoreSettings;
